import {
  ForecastAccount, CashFlowItem, ForecastSettings, MonthlyProjection,
} from '../types/personalForecast';

/**
 * Carry a Personal Forecast across to the Retirement Planner.
 *
 * The Forecast knows the balances, when the work stops and what is actually spent; the Planner
 * knows how to optimise a drawdown. Retyping twenty figures between them is why people answer
 * retirement questions with a spreadsheet instead. Nothing here is a guess presented as fact: what
 * is inferred is reported alongside, so it can be corrected before anything is calculated.
 */
export type AccountType = NonNullable<ForecastAccount['accountType']>;

export interface RetirementHandoff {
  age: number;
  retirementAge: number;
  currentYear: number;
  rrspBalance: number;
  tfsaBalance: number;
  nonRegisteredBalance: number;
  rrspMonthlyContribution: number;
  tfsaMonthlyContribution: number;
  nonRegisteredMonthlyContribution: number;
  annualSpending: number;
  /** Anything worked out rather than read, so the user can check it before relying on it. */
  assumptions: string[];
}

const RRSP_WORDS = ['rrsp', 'rrif', 'lira', 'locked-in'];
const TAXABLE_WORDS = ['brokerage', 'non-registered', 'nonregistered', 'non registered',
  'margin', 'taxable', 'investment account', 'trading'];
const PROPERTY_WORDS = ['house', 'home', 'propert', 'condo'];
/** Not yours to draw down, or not an investment account at all. */
const NOT_AN_ACCOUNT = ['cpp', 'oas', 'resp', 'benefit', 'receivable', 'car', 'vehicle',
  'mortgage', 'loan', 'selling cost', 'offset', 'revaluation'];

/**
 * What kind of account this looks like, when nobody has said.
 *
 * Deliberately cautious: anything without a clear signal comes back as `other` and is left out of
 * the handoff rather than swept into a balance. Guessing wrong here is expensive - a CPP entitlement
 * counted as an RRSP would be drawn down and taxed like one, and a car counted as a non-registered
 * portfolio would be spent. Being told an account was skipped costs the user a dropdown.
 */
export const inferAccountType = (a: ForecastAccount): AccountType => {
  if (a.accountType) return a.accountType;
  const name = (a.name || '').toLowerCase();
  if (a.side === 'liability') return 'other';
  if (NOT_AN_ACCOUNT.some(w => name.includes(w))) {
    return PROPERTY_WORDS.some(w => name.includes(w)) ? 'property' : 'other';
  }
  if (name.includes('tfsa')) return 'tfsa';
  if (RRSP_WORDS.some(w => name.includes(w))) return 'rrsp';
  if (PROPERTY_WORDS.some(w => name.includes(w))) return 'property';
  if (TAXABLE_WORDS.some(w => name.includes(w))) return 'nonRegistered';
  // A retirement-bucket account taxed on the way out is an RRSP in all but name.
  if (a.bucket === 'retirement' && (a.afterTaxPercent ?? 100) < 100) return 'rrsp';
  return 'other';
};

export function buildRetirementHandoff(
  accounts: ForecastAccount[],
  cashFlows: CashFlowItem[],
  settings: ForecastSettings,
  projections: MonthlyProjection[],
): RetirementHandoff {
  const assumptions: string[] = [];
  const forecast = projections.filter(p => p.source === 'forecast');
  const first = forecast[0] || projections[projections.length - 1];
  const currentYear = first ? Number(first.month.slice(0, 4)) : new Date().getFullYear();
  const age = settings.birthYear ? currentYear - settings.birthYear : 0;
  if (!settings.birthYear) assumptions.push('No birth year set, so age could not be filled in.');

  const typeOf = new Map(accounts.map(a => [a.id, inferAccountType(a)]));
  const untyped = accounts.filter(a => !a.accountType && typeOf.get(a.id) !== 'other');
  if (untyped.length) {
    assumptions.push(`${untyped.length} account type${untyped.length === 1 ? ' was' : 's were'} read from the name: `
      + untyped.map(a => `${a.name} → ${typeOf.get(a.id)}`).join(', '));
  }
  // Said out loud, because a balance quietly left out is worse than one wrongly included.
  const skipped = accounts.filter(a =>
    a.side === 'asset' && a.bucket !== 'current' && typeOf.get(a.id) === 'other'
    && Math.abs(first?.accounts[a.id]?.closingBalance ?? 0) > 1);
  if (skipped.length) {
    assumptions.push(`${skipped.length} account${skipped.length === 1 ? '' : 's'} could not be classified and ${skipped.length === 1 ? 'is' : 'are'} left out: `
      + skipped.map(a => a.name).join(', ') + '. Set a type on each one if it belongs in the plan.');
  }

  const balanceOf = (want: AccountType) => accounts
    .filter(a => a.side === 'asset' && typeOf.get(a.id) === want)
    .reduce((sum, a) => sum + (first?.accounts[a.id]?.closingBalance ?? 0), 0);

  // Money arriving in an account of that kind, whatever route it took to get there.
  const contributionTo = (want: AccountType) => cashFlows
    .filter(cf => {
      const dest = cf.toAccountId || (cf.isInflow ? cf.accountId : null);
      return dest ? typeOf.get(dest) === want : false;
    })
    .reduce((sum, cf) => sum + (first?.flows[cf.id] ?? 0), 0);

  // What actually leaves for good each month - transfers to your own accounts are not spending.
  const spend = forecast.slice(0, 12);
  const monthlySpend = spend.length
    ? spend.reduce((sum, p) => sum + cashFlows.reduce((s, cf) => {
        if (cf.isInflow || cf.toAccountId) return s;
        const acct = accounts.find(a => a.id === cf.accountId);
        return acct?.bucket === 'current' ? s + (p.flows[cf.id] ?? 0) : s;
      }, 0), 0) / spend.length
    : 0;

  // The Planner models a spouse in full - balances, income stages, and pension splitting from 65,
  // which its optimiser searches. The Forecast has no notion of one, and no way to tell whether it
  // describes one person or a household: the same accounts could be yours or both of yours. Tax is
  // per person, so that distinction changes the answer, and it has to be asked rather than assumed
  // in either direction.
  const mentionsPartner = cashFlows.some(cf =>
    /spouse|wife|husband|partner/i.test(cf.name || ''));
  if (mentionsPartner) {
    assumptions.push('Your forecast mentions a partner, but everything crosses over as one '
      + "taxpayer's. If these are household balances they belong to two people, and the Planner "
      + 'will tax them as one. Split them across the spouse section before you calculate - pension '
      + 'splitting after 65 can change the answer materially.');
  }

  // Work stops when the last salary or bonus does. An item with no end date means it never stops,
  // which is not a retirement age - so that is reported rather than turned into one.
  const working = cashFlows.filter(cf =>
    cf.isInflow && !cf.toAccountId && ['Salary', 'Bonus'].includes(cf.category));
  const neverEnds = working.some(cf => !cf.endDate);
  const lastEnd = working.map(cf => cf.endDate).filter(Boolean).sort().pop();
  let retirementAge = 65;
  if (neverEnds) {
    assumptions.push('Some employment income has no end date, so retirement age defaults to 65.');
  } else if (lastEnd && settings.birthYear) {
    retirementAge = Number(lastEnd.slice(0, 4)) - settings.birthYear + 1;
  } else {
    assumptions.push('Retirement age defaults to 65.');
  }

  return {
    age,
    retirementAge,
    currentYear,
    rrspBalance: Math.round(balanceOf('rrsp')),
    tfsaBalance: Math.round(balanceOf('tfsa')),
    nonRegisteredBalance: Math.round(balanceOf('nonRegistered')),
    rrspMonthlyContribution: Math.round(contributionTo('rrsp')),
    tfsaMonthlyContribution: Math.round(contributionTo('tfsa')),
    nonRegisteredMonthlyContribution: Math.round(contributionTo('nonRegistered')),
    annualSpending: Math.round(monthlySpend * 12),
    assumptions,
  };
}

/** Where a handoff waits while the browser moves between the two tools. */
export const HANDOFF_KEY = 'larryshelp_forecast_to_retirement';

export const stashHandoff = (h: RetirementHandoff) => {
  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(h));
  } catch {
    // Storage unavailable (private mode, quota): the planner simply opens empty.
  }
};

/** Read it once and clear it, so a back-and-forth does not silently re-apply old figures. */
export const takeHandoff = (): RetirementHandoff | null => {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(HANDOFF_KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
